import {Panel} from '@github-ui/docked-side-panel/Panel'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentIcon, HistoryIcon} from '@primer/octicons-react'
import {Button, PortalContext, registerPortalRoot} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {KeyboardEvent, MouseEvent, ReactNode, RefObject} from 'react'
import {Suspense, useCallback, useEffect, useId, useMemo, useRef} from 'react'
import {createPortal} from 'react-dom'

import {IssueTimelineErrorFallback} from '../IssueTimelineErrorFallback'
import {useActivityPanel} from './ActivityPanelContext'
import {ActivityLoadingState} from './ActivityTimelineControls'
import styles from './TimelineActivityMenu.module.css'
import {useAnnounceOnMount} from './use-announce-on-mount'

const COMMENT_UNAVAILABLE = 'Comment unavailable. This comment cannot be shown in the activity timeline.'

export function ActivityEmptyState() {
  return (
    <Blankslate>
      <Blankslate.Visual>
        <CommentIcon size={24} />
      </Blankslate.Visual>
      <Blankslate.Heading as="h3">No activity</Blankslate.Heading>
      <Blankslate.Description>Activity on this issue will appear here.</Blankslate.Description>
    </Blankslate>
  )
}

export function ActivityTargetUnavailable() {
  const emptyStateRef = useAnnounceOnMount<HTMLDivElement>(COMMENT_UNAVAILABLE)

  return (
    <div ref={emptyStateRef}>
      <Blankslate>
        <Blankslate.Visual>
          <CommentIcon size={24} />
        </Blankslate.Visual>
        <Blankslate.Heading as="h3">Comment unavailable</Blankslate.Heading>
        <Blankslate.Description>This comment cannot be shown in the activity timeline.</Blankslate.Description>
      </Blankslate>
    </div>
  )
}

type TimelineActivityMenuProps = {
  initiallyOpen: boolean | (() => boolean)
  legacyHeader: boolean
  timeline: ReactNode
  /** Controls shown next to the panel heading, such as filter and sort menus. */
  headerActions?: ReactNode
  /** Shown above the timeline. */
  notice?: ReactNode
  /** Remounts the timeline when it changes, clearing an error it has shown. */
  timelineKey?: string
}

function createReturnFocusRef(
  target: HTMLElement | undefined,
  fallbackRef: RefObject<HTMLElement | null>,
): RefObject<HTMLElement | null> {
  return {
    // Resolve on close because the opener may have unmounted, or (e.g. the legacy header's sticky
    // toggle while scrolled to the top) still be connected but hidden with `display: none`.
    get current() {
      return target?.isConnected && target.checkVisibility() ? target : fallbackRef.current
    },
  }
}

export function TimelineActivityMenu({
  initiallyOpen,
  legacyHeader,
  timeline,
  headerActions,
  notice,
  timelineKey,
}: TimelineActivityMenuProps) {
  const {closePanel, open, openPanel, panelTarget, commentJump, opener} = useActivityPanel()
  const initialOpenStateAppliedRef = useRef(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const dockedReturnFocusRef = useMemo(() => createReturnFocusRef(opener, buttonRef), [opener])

  useEffect(() => {
    if (!open || !panelTarget) return

    if (commentJump) {
      // A ready comment takes focus on the next frame, after the panel's focus trap is active.
      headingRef.current?.focus()
      return
    }

    // `opener` is only supplied by an explicit, user-triggered open (a toggle click), not by the
    // mount-time `initiallyOpen` restoration. At desktop widths the panel renders as a non-modal
    // region, so nothing else moves focus into it; without this, focus stays on the toggle that
    // opened it (or is lost entirely if a container-width collapse then unmounts that toggle).
    if (!opener) return

    const frame = requestAnimationFrame(() => closeButtonRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [open, panelTarget, commentJump, opener])

  const portalContainerName = useId()
  const portalContext = useMemo(() => ({portalContainerName}), [portalContainerName])
  const registerActivityPortalRoot = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) registerPortalRoot(node, portalContainerName)
    },
    [portalContainerName],
  )

  useEffect(() => {
    if (initialOpenStateAppliedRef.current) return
    initialOpenStateAppliedRef.current = true
    if (typeof initiallyOpen === 'function' ? initiallyOpen() : initiallyOpen) openPanel()
  }, [initiallyOpen, openPanel])

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      openPanel({opener: event.currentTarget})
      sendEvent('issue_viewer.activity_panel.opened', {source: 'activity_button'})
    },
    [openPanel],
  )

  const handleClose = useCallback(() => {
    if (!open) return
    closePanel()
    sendEvent('issue_viewer.activity_panel.closed')
  }, [closePanel, open])

  const handleDockedKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Handle Escape before the containing issue side panel dismisses the issue.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const eventTarget = event.target
      if (eventTarget instanceof Element) {
        if (eventTarget.closest('[role="menu"]')) return
        const dialog = eventTarget.closest('[role="dialog"]')
        if (dialog && !dialog.contains(closeButtonRef.current)) return
      }
      event.preventDefault()
      event.stopPropagation()
      const returnTarget = dockedReturnFocusRef.current
      handleClose()
      requestAnimationFrame(() => {
        const connectedTarget = returnTarget?.isConnected ? returnTarget : buttonRef.current
        connectedTarget?.focus()
      })
    },
    [dockedReturnFocusRef, handleClose],
  )

  const panelContent = (
    <>
      {notice}
      <ErrorBoundary key={timelineKey} boundaryName="issue-activity-timeline" fallback={<IssueTimelineErrorFallback />}>
        <Suspense fallback={<ActivityLoadingState />}>{timeline}</Suspense>
      </ErrorBoundary>
    </>
  )
  const actions = headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null

  return (
    <>
      <Button ref={buttonRef} leadingVisual={HistoryIcon} onClick={handleOpen}>
        Activity
      </Button>
      {open &&
        panelTarget &&
        createPortal(
          <PortalContext value={portalContext}>
            <div className="height-full" onKeyDownCapture={handleDockedKeyDown}>
              <Panel
                heading="Activity"
                headingLevel={2}
                headingRef={headingRef}
                headingTabIndex={commentJump ? -1 : undefined}
                headerActions={actions}
                closeButtonRef={closeButtonRef}
                returnFocusRef={dockedReturnFocusRef}
                onClose={handleClose}
                headerClassName={clsx(styles.dockedHeader, legacyHeader && styles.legacyDockedHeader)}
              >
                <Panel.Header>
                  <div ref={registerActivityPortalRoot} />
                </Panel.Header>
                <Panel.Body className={styles.body}>{panelContent}</Panel.Body>
              </Panel>
            </div>
          </PortalContext>,
          panelTarget,
        )}
    </>
  )
}
